import { useRoles } from "../../dashboard/forms/improved_components/hooks/use_roles";
import { WithTooltipPortaled } from "../../dashboard/forms/improved_components/tooltips/with_tooltip_portaled";
import { ELIGIBLE_ROLES_SETTINGS } from "./eligible_roles_settings_item";
import chroma from "chroma-js";

// Calculate if text should be black or white based on background color brightness
function getTextColor(backgroundColor: string): string {
	const color = chroma(backgroundColor);
	const luminance = color.luminance();
	return luminance > 0.5 ? "black" : "white";
}

type RoleTagProps = {
	role: {
		id: string;
		name: string;
		color?: string;
	};
};

function RoleTag({ role }: RoleTagProps) {
	const roleColor = role.color ? `${role.color}` : "#99AAB5"; // Default Discord color
	const roleColorWithAlpha = chroma(roleColor).alpha(0.8);
	const bgColor = roleColorWithAlpha.css();
	const textColor = getTextColor(bgColor);

	return (
		<span
			className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium"
			style={{
				backgroundColor: bgColor,
			}}
		>
			<p style={{ color: textColor, opacity: 0.6 }} className="font-bold">
				@{role.name}
			</p>
		</span>
	);
}

export function EligibleRolesIndicator({
	eligibleRoleIds,
}: {
	eligibleRoleIds: string[];
}) {
	const roles = useRoles();

	if (!eligibleRoleIds || eligibleRoleIds.length === 0) {
		return null;
	}

	const eligibleRoles = roles
		? eligibleRoleIds
				.map((id) => roles.find((role) => role.id === id))
				.filter((role) => role) // Filter out undefined roles
		: [];

	const roleNamesString =
		eligibleRoles.length > 0
			? eligibleRoles.map((role) => role.name).join(", ")
			: "Loading roles...";

	// Create tooltip JSX content with colored role badges
	const tooltipContent = (
		<div>
			<div className="mb-2">{ELIGIBLE_ROLES_SETTINGS.description}</div>
			<div className="mb-1">Restricted to roles:</div>
			<div className="flex flex-wrap items-center justify-center gap-1">
				{eligibleRoles.map((role) => (
					<RoleTag key={role.id} role={role} />
				))}
			</div>
		</div>
	);

	return (
		<WithTooltipPortaled style="Info" tooltip={tooltipContent} no_delay>
			<span className="inline-flex items-center rounded-full bg-base-dark px-2 py-1 text-xs font-medium text-sesh-primary">
				<i className="fas fa-user-shield mr-1"></i>
				{ELIGIBLE_ROLES_SETTINGS.title}
			</span>
		</WithTooltipPortaled>
	);
}
